import { useEffect, useLayoutEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  applyBackNavigationScroll,
  applyForwardNavigationScroll,
  clearLegacyScrollKeys,
  consumePendingNavigationAction,
  getLastNavigationAction,
  getScrollKey,
  isScrollRestorableRoute,
  saveScrollPosition,
  setPendingNavigationAction,
} from "../utils/scroll-restoration";

export default function ScrollRestoration() {
  const history = useHistory();
  const location = useLocation();
  const routeKeyRef = useRef(getScrollKey(location.pathname, location.search));
  const pathnameRef = useRef(location.pathname);
  const searchRef = useRef(location.search);
  const previousPathnameRef = useRef(location.pathname);
  const restoreCleanupRef = useRef(null);
  const handledPopInLayoutRef = useRef(false);
  const skipInitialPageShowRef = useRef(true);

  const setRestoreCleanup = (cleanup) => {
    if (restoreCleanupRef.current) {
      restoreCleanupRef.current();
    }

    restoreCleanupRef.current = cleanup || null;
  };

  const syncRouteRefs = (pathname, search) => {
    pathnameRef.current = pathname;
    searchRef.current = search;
    routeKeyRef.current = getScrollKey(pathname, search);
  };

  useEffect(() => {
    clearLegacyScrollKeys();

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      saveScrollPosition(routeKeyRef.current, window.scrollY);
      setPendingNavigationAction("POP");
      handledPopInLayoutRef.current = false;

      const { pathname, search } = window.location;
      syncRouteRefs(pathname, search);
    };

    const onPageShow = (event) => {
      if (skipInitialPageShowRef.current) {
        skipInitialPageShowRef.current = false;
        return;
      }

      const { pathname, search } = window.location;
      syncRouteRefs(pathname, search);

      const shouldHandle =
        event.persisted || getLastNavigationAction() === "POP";

      if (!shouldHandle) {
        return;
      }

      setRestoreCleanup(applyBackNavigationScroll(pathname, search));
      handledPopInLayoutRef.current = true;
    };

    const onPageHide = () => {
      saveScrollPosition(routeKeyRef.current, window.scrollY);
    };

    window.addEventListener("popstate", onPopState);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  useEffect(() => {
    return history.listen((locationUpdate, action) => {
      saveScrollPosition(routeKeyRef.current, window.scrollY);
      setPendingNavigationAction(action);
      syncRouteRefs(locationUpdate.pathname, locationUpdate.search);

      if (action === "POP") {
        handledPopInLayoutRef.current = false;
      }
    });
  }, [history]);

  useLayoutEffect(() => {
    const navigationAction =
      consumePendingNavigationAction() || history.action;
    const pathnameChanged =
      previousPathnameRef.current !== location.pathname;

    syncRouteRefs(location.pathname, location.search);

    if (navigationAction === "POP") {
      handledPopInLayoutRef.current = true;
      setRestoreCleanup(
        applyBackNavigationScroll(location.pathname, location.search)
      );
    } else {
      applyForwardNavigationScroll(pathnameChanged);
    }

    previousPathnameRef.current = location.pathname;
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    const canSave = isScrollRestorableRoute(
      location.pathname,
      location.search
    );

    if (!canSave) {
      return undefined;
    }

    const persistScroll = () => {
      saveScrollPosition(routeKeyRef.current, window.scrollY);
    };

    let ticking = false;

    const onScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      requestAnimationFrame(() => {
        persistScroll();
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      persistScroll();
      window.removeEventListener("scroll", onScroll);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    return () => {
      if (restoreCleanupRef.current) {
        restoreCleanupRef.current();
      }
    };
  }, []);

  return null;
}
